/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useMemo} from "react";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import BookingAction, {PackageCompetition} from "store/booking/BookingAction";
import {GroupedCompetitionItem} from "types/entities/GroupedPackageItem";
import {PackageItemCompetition} from "types/entities/PackageItem";
import {translate} from "utils/utility";
import {Translations} from "helpers/config";

export enum CompetitionCategoryType {
    A = "A",
    B = "B",
    C = "C",
}

export const normalizeCategoryType = (categoryType?: string | null): string => (categoryType || "").trim().toUpperCase();

// sort by competition.category_type first, then by position (empty category / null position last)
const compareCompetitions = (a: GroupedCompetitionItem, b: GroupedCompetitionItem): number => {
    const aCategory = normalizeCategoryType(a.competition?.category_type);
    const bCategory = normalizeCategoryType(b.competition?.category_type);

    if (aCategory !== bCategory) {
        if (!aCategory) return 1;
        if (!bCategory) return -1;
        return aCategory.localeCompare(bCategory);
    }

    if (a.position === null && b.position === null) return 0;
    if (a.position === null) return 1;
    if (b.position === null) return -1;
    return a.position - b.position;
};

export const filterCompetitionsByCategoryTypes = (list: GroupedCompetitionItem[], categoryTypes: CompetitionCategoryType[]): GroupedCompetitionItem[] => {
    const allowed = categoryTypes.map(categoryType => normalizeCategoryType(categoryType));
    return (list || []).filter(item => allowed.includes(normalizeCategoryType(item.competition?.category_type)));
};

// competitions included in the package - the customer picks from them for free
export const getFreeSelectCompetitions = (list: GroupedCompetitionItem[], freeCategories: string[]): GroupedCompetitionItem[] => {
    const allowed = freeCategories.map(c => normalizeCategoryType(c));
    return (list || []).filter(item => allowed.includes(normalizeCategoryType(item.competition?.category_type)));
};

export interface CompetitionCategory {
    category: string;
    title: string;
    description: string;
    details: string[];
}

const COMPETITIONS: CompetitionCategory[] = [
    {
        title: "Grand Prix Classic",
        category: "grand-prix-classic",
        description:
            "Bereits aus einer Kartfahrt entsteht ein <strong>spannendes Rennformat mit Ranking</strong> nach Bestzeiten und feierlicher <strong>Siegerehrung für die Top 3 mit Pokalen und Siegerfoto.</strong>",
        details: [
            "<strong>Priority Access</strong> Kartbahn und <strong>Competition-Modus</strong> mit extra Speed",
            "<strong>Race-Briefing</strong> durch die Rennleitung",
            "<strong>Ranking</strong> der schnellsten Rundenzeiten",
            "<strong>Siegerehrung</strong> auf dem Podest mit <strong>3 Pokalen und Siegerföteli</strong>",
        ],
    },
    {
        title: "Grand Prix Deluxe",
        category: "grand-prix-deluxe",
        description:
            "<strong>Hier kommen Kartfans ins Schwärmen!</strong> Der Grand Prix Deluxe bringt den schnellsten Fahrern aus zwei Qualifying-Rennen <strong>zusätzlich ein Grande Finale als Highlight.</strong>",
        details: [
            "<strong>Priority Access</strong> Kartbahn und <strong>Competition-Modus</strong> mit extra Speed",
            "<strong>Race Briefing</strong> durch die Rennleitung",
            "<strong>Exklusives Grande Finale</strong> mit zusätzlicher Race Time für die schnellsten 4–8 Fahrer",
            "<strong>Siegerehrung</strong> auf dem Podest für die <strong>Top 3</strong> mit <strong>Pokalen und Siegerföteli</strong>",
        ],
    },
    {
        title: "Tricky Boxes Competition",
        category: "tricky-boxes",
        description:
            "<strong>Welches Team beweist ein kühles Köpfchen an unseren Rätselboxen?</strong> Es entsteht ein dynamischer <strong>Teamwettkampf</strong>, bei dem der clevere Einsatz der ArboJoker über den Sieg entscheidet.",
        details: [
            "12 Tricky Boxen mit <strong>raffinierten Rätseln</strong> in 2er-4er Teams",
            "Competition <strong>Briefing</strong> und <strong>Coaching durch Game Host</strong>",
            "<strong>ArboJoker</strong> für taktische Vorteile &amp; Tipps",
            "<strong>Ranking</strong> und <strong>Siegerehrung</strong> auf dem Podest für die <strong>Top 3 Teams</strong> mit <strong>Pokalen und Siegerföteli</strong>",
        ],
    },
    {
        title: "Cube Challenges Competition",
        category: "cube-challenges",
        description:
            "<strong>18 Themenräume mit spannenden Missionen</strong> warten auf eure Teams. Auf der Highscore-Jagd triumphieren diejenigen, die <strong>Skill, Brain und Teamgeist beweisen.</strong>",
        details: [
            "18 escape room-artige <strong>Adventure Rooms</strong> in 3er-4er Teams",
            "Competition <strong>Briefing durch Game Host</strong>",
            "Für <strong>grosse Gruppen</strong> geeignet",
            "<strong>Ranking</strong> und <strong>Siegerehrung</strong> auf dem Podest für die <strong>Top 3 Teams</strong> mit <strong>Pokalen und Siegerföteli</strong>",
        ],
    },
    {
        title: "Pixel Gaming Floor Competition",
        category: "pixel-gaming-floor",
        description:
            "<strong>Zeigt als Team euer Können</strong> auf der Pixel-Spielfläche. Hier zählen <strong>Geschicklichkeit, Reaktion und perfektes Zusammenspiel</strong>, um den Highscore zu holen.",
        details: [
            "<strong>Geschicklichkeits-Challenges</strong> in 2er-4er Teams",
            "Competition <strong>Briefing</strong> und <strong>Coaching durch Game Host</strong>",
            "Dynamischer Wettkampf mit <strong>garantiertem Lachfaktor</strong>",
            "<strong>Ranking</strong> und <strong>Siegerehrung</strong> auf dem Podest für die <strong>Top 3 Teams</strong> mit <strong>Pokalen und Siegerföteli</strong>",
        ],
    },
    {
        title: "Speed Climbing Competition",
        category: "speed-climbing",
        description:
            "<strong>Klettert im Wettkampfmodus und feuert euch im Team</strong> gegenseitig zu Höchstleistungen an. Technik und Teamspirit sind entscheidend, um euren Gegnern im Kampf um die Bestzeit davonzuklettern.",
        details: [
            "Dynamisch, aber für jedes Team machbar: <strong>drei Runs pro Team</strong>",
            "Competition <strong>Briefing durch Game Host</strong>",
            "<strong>Teamwertung:</strong> holt gemeinsam die Bestzeit",
            "<strong>Ranking</strong> und <strong>Siegerehrung</strong> auf dem Podest für die <strong>Top 3 Teams</strong> mit <strong>Pokalen und Siegerföteli</strong>",
        ],
    },
    {
        title: "Interactive MiniGolf Competition",
        category: "interactive-minigolf",
        description:
            "<strong>Mit Präzision, Nervenstärke und Teamwork zum Highscore.</strong> Beweist den anderen Teams, dass ihr die wahren Interactive-Minigolf-Champions seid!",
        details: [
            "8 interaktive <strong>Minigolfbahnen im Wettkampfmodus</strong>",
            "Competition <strong>Briefing durch Game Host</strong>",
            "<strong>Teamwertung</strong>: holt gemeinsam den Highscore",
            "<strong>Ranking</strong> und <strong>Siegerehrung</strong> auf dem Podest für die <strong>Top 3 Teams</strong> mit <strong>Pokalen und Siegerföteli</strong>",
        ],
    },
];

const getCompetitionFromCategory = (category: string) => {
    return COMPETITIONS.find(com => com.category === category);
};

const groupCompetitionItems = (list: PackageItemCompetition[]): GroupedCompetitionItem[] => {
    const groupedItems = (list || []).reduce((acc: GroupedCompetitionItem[], item): GroupedCompetitionItem[] => {
        const {variant_id, is_variant, extra, image, position, name, description, description_html, packageItemVariant, variant_description, competition} =
            item;

        const competitionCategory = getCompetitionFromCategory(competition?.type);
        console.log(extra.groupName, competition);

        if (!is_variant) {
            acc.push({
                groupName: extra.groupName,
                variant_id: null,
                items: [item],
                image,
                position,
                name,
                description: competitionCategory ? ({en: competitionCategory?.description, de: competitionCategory.description} as Translations) : description,
                description_html: competitionCategory ? (competitionCategory?.details.join("\r\n") as unknown as Translations) : description_html,
                packageItemVariant: null,
                is_variant: false,
                variant_description: null,
                competition,
            });
            return acc;
        }

        const existingGroup = acc.find(group => group.variant_id === variant_id);
        if (!existingGroup) {
            acc.push({
                groupName: extra.groupName,
                variant_id,
                items: [item],
                image: packageItemVariant?.image || image,
                position,
                name: packageItemVariant?.name || name,
                description: competitionCategory ? ({en: competitionCategory?.description, de: competitionCategory.description} as Translations) : description,
                description_html: competitionCategory ? (competitionCategory?.details.join("\r\n") as unknown as Translations) : description_html,
                packageItemVariant,
                is_variant,
                variant_description: translate(variant_description),
                competition,
            });
        } else {
            existingGroup.items.push(item);
            if (!existingGroup.image) existingGroup.image = packageItemVariant?.image || image;
            if (existingGroup.position === null) existingGroup.position = position;
            if (!existingGroup.name) existingGroup.name = packageItemVariant?.name || name;
            if (!existingGroup.description) existingGroup.description = packageItemVariant?.description || description;
            if (!existingGroup.description_html) existingGroup.description_html = packageItemVariant?.description_html || description_html;
            if (!existingGroup.competition) existingGroup.competition = competition;
        }

        return acc;
    }, []);

    return groupedItems.sort(compareCompetitions);
};

const mapCompetitionItemsIndividually = (list: PackageItemCompetition[]): GroupedCompetitionItem[] => {
    return (list || [])
        .map(item => {
            const competitionCategory = getCompetitionFromCategory(item?.competition.type);

            return {
                groupName: item.extra.groupName,
                variant_id: item.variant_id,
                items: [item],
                image: item?.packageItemVariant?.image || item.image,
                position: item.position,
                name: item.name,
                description: competitionCategory
                    ? ({en: competitionCategory?.description, de: competitionCategory.description} as Translations)
                    : item.description,
                description_html: competitionCategory ? (competitionCategory?.details.join("\r\n") as unknown as Translations) : item.description_html,
                packageItemVariant: null,
                is_variant: false,
                variant_description: null,
                competition: item.competition,
            };
        })
        .sort(compareCompetitions);
};
export const isCompetitionInGroups = (groups: GroupedCompetitionItem[], competition: PackageItemCompetition): boolean =>
    groups.some(group =>
        group.items.some(item =>
            item.guid && competition.guid ? item.guid === competition.guid : item.id === competition.id && item.article_id === competition.article_id
        )
    );

export const useCompetitions = () => {
    const selectedCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.competition);
    const packageInfo = useTypedSelector(state => state.bookingState.bookingProcess.packageInfo);
    const availableCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.availableCompetitionItems);
    const availableIncludedCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.availableIncludedCompetitionItems);

    const freeCompetitionsCategories: string[] = useMemo(() => packageInfo?.included_competitions?.types || [], [packageInfo]);
    const freeCompetitionsCount: number = packageInfo?.included_competitions?.total || 0;

    const selectedFreeCompetitions = useMemo(
        () => selectedCompetitions.filter(competition => competition.is_free && competition.packageItem.id !== 0),
        [selectedCompetitions]
    );

    const renounceFreeCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.renounceFreeCompetitions);
    const dispatch = useDispatch();

    const removeNewlyAddedPaidCompetitions = (competitions: PackageCompetition[]) => {
        const includedTypes = new Set((availableIncludedCompetitions || []).map(item => normalizeCategoryType(item.competition?.type)).filter(Boolean));

        const toRemove = competitions.filter(
            c => !c.is_free && c.packageItem.id !== 0 && includedTypes.has(normalizeCategoryType(c.packageItem.competition?.type))
        );

        toRemove.forEach(c => {
            dispatch(BookingAction.removeCompetition(c));
        });
    };

    const selectedFreeCompetitionsCount = selectedFreeCompetitions.length;

    const groupedCompetitionItems = useMemo(() => groupCompetitionItems(availableCompetitions || []), [availableCompetitions]);

    const includedCompetitionItems = useMemo(() => mapCompetitionItemsIndividually(availableIncludedCompetitions || []), [availableIncludedCompetitions]);

    // The backend has already separated included choices from paid extras.
    // `included_competitions.total` limits the selection count; it does not filter this list.
    const freeSelectCompetitions = includedCompetitionItems;

    const competitionItems = useMemo(() => {
        const hasCompletedIncludedSelection =
            freeCompetitionsCount === 0 || selectedFreeCompetitions.length >= freeCompetitionsCount || renounceFreeCompetitions;

        // Before the included choice is complete, hide only paid equivalents of
        // included competitions. Afterwards, hide only the types chosen for free.
        const excludedTypes = new Set(
            (hasCompletedIncludedSelection ? selectedFreeCompetitions.map(selected => selected.packageItem) : availableIncludedCompetitions || [])
                .map(item => normalizeCategoryType(item.competition?.type))
                .filter(Boolean)
        );
        if (excludedTypes.size === 0) return groupedCompetitionItems;

        return groupedCompetitionItems
            .map(group => ({
                ...group,
                items: group.items
                    .filter(item => !excludedTypes.has(normalizeCategoryType(item.competition?.type)))
                    .sort((a, b) => {
                        if (a.position && b.position) {
                            return a.position - b.position;
                        }
                        return 0;
                    }),
            }))
            .filter(group => group.items.length > 0);
    }, [availableIncludedCompetitions, freeCompetitionsCount, groupedCompetitionItems, renounceFreeCompetitions, selectedFreeCompetitions]);

    const paidCompetitions = competitionItems;

    return {
        selectedCompetitions,
        freeCompetitionsCategories,
        freeCompetitionsCount,
        selectedFreeCompetitions,
        selectedFreeCompetitionsCount,
        renounceFreeCompetitions,
        competitionItems,
        includedCompetitionItems,
        freeSelectCompetitions,
        paidCompetitions,
        removeNewlyAddedPaidCompetitions,
    };
};
